import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input, type InputProps } from './input';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Classes for the wrapper that positions the toggle. */
  wrapperClassName?: string;
  /** Accessible names of the toggle; defaults are "Show password"/"Hide password". */
  showLabel?: string;
  hideLabel?: string;
}

/**
 * `Input` for passwords with an eye toggle that shows/hides what was typed.
 *
 * The toggle is a real `type="button"` (never submits the form), named
 * "Show password"/"Hide password" with `aria-pressed`, and sits inside the
 * field (the input reserves `pr-12`) so nothing shifts when it flips. A
 * pointer press doesn't take focus from the field, and the caret/selection
 * survives the type switch. Everything else (ref, autocomplete, size,
 * `aria-invalid`, react-hook-form `register`) goes to the input unchanged.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      wrapperClassName,
      showLabel = 'Show password',
      hideLabel = 'Hide password',
      inputSize,
      disabled,
      id,
      ...props
    },
    ref,
  ) => {
    const [visible, setVisible] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const selection = React.useRef<[number | null, number | null] | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const toggle = () => {
      const input = inputRef.current;
      selection.current =
        input && document.activeElement === input
          ? [input.selectionStart, input.selectionEnd]
          : null;
      setVisible((v) => !v);
    };

    // Chrome moves the caret to the start after React swaps the type, so put
    // it back now and again on the next frame, once that reset has happened.
    React.useLayoutEffect(() => {
      const input = inputRef.current;
      const saved = selection.current;
      selection.current = null;
      if (!input || !saved || saved[0] === null || saved[1] === null) return undefined;
      const [start, end] = saved;
      const restore = () => {
        if (document.activeElement !== input) return;
        try {
          input.setSelectionRange(start, end);
        } catch {
          // Types without selection support: nothing to restore.
        }
      };
      restore();
      const frame = requestAnimationFrame(restore);
      return () => cancelAnimationFrame(frame);
    }, [visible]);

    const large = inputSize === 'lg';
    return (
      <div className={cn('relative w-full', wrapperClassName)}>
        <Input
          ref={setRefs}
          id={id}
          type={visible ? 'text' : 'password'}
          inputSize={inputSize}
          disabled={disabled}
          className={cn('pr-12', className)}
          {...props}
        />
        <button
          type='button'
          onClick={toggle}
          // Keep focus (and the caret) in the field on a pointer press.
          onMouseDown={(event) => event.preventDefault()}
          disabled={disabled}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          aria-controls={id}
          className={cn(
            'absolute right-1 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-global-green focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
            large ? 'h-10 w-10' : 'h-8 w-8',
          )}
        >
          {visible ? (
            <EyeOff className='h-5 w-5' aria-hidden='true' />
          ) : (
            <Eye className='h-5 w-5' aria-hidden='true' />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
